import { useRef, useState } from "react";
import { AdvertisementTypeEnum } from "@app/types/enums";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import { renderPageTitleV2 } from "@components/MetaTags/helpers";
import MetaTags from "@components/MetaTags/MetaTags";
import {
  DAILY_NEWSLETTERS,
  PREMIUM_NEWSLETTERS,
  WEEKLY_NEWSLETTERS,
} from "@components/Newsletter/constants";
import NewsletterSubscriptionPanel from "@components/Newsletter/NewsletterSubscriptionPanel";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import useHandleSelectedNewsletter from "@hooks/useHandleSelectedNewsletters";
import NewsletterAnchorLinks from "@pages/Newsletter/SignUp/NewsletterAnchorLinks";
import NewsletterSections from "@pages/Newsletter/SignUp/NewsletterSections";
import Container from "@src/app/components/Container/Container";
import { GoogleAdsSlotFactory, scrollToNewsletterSection } from "@util/helpers";
import cx from "classnames";

export default function NewsletterSignupPage() {
  const { isPrestitialEnabled, topOverlayImpressions, topOverlayValidity } =
    useGlobalAdSetting();
  const [email, setEmail] = useState("");
  const premiumNewsletterRef = useRef<HTMLDivElement>(null);
  const dailyNewsletterRef = useRef<HTMLDivElement>(null);
  const weeklyNewsletterRef = useRef<HTMLDivElement>(null);

  const [selectedNewsletters, handleSelectedNewsletter] =
    useHandleSelectedNewsletter();

  return (
    <div className="w-full">
      <MetaTags
        title={renderPageTitleV2({
          kind: "static",
          title: "Newsletter signup",
        })}
        description="Read more at The Business Times."
        slug="/newsletter/sign-up"
      />

      <GAData
        title="Newsletter_signup_landing_page"
        level2="newsletter"
        contentcat={1}
      />

      <Advertisement
        rootClassName="min-h-[90px] bg-gray-125 py-1 md:py-6 border-y border-gray-175"
        adUnitProps={{
          type: AdvertisementTypeEnum.LB1,
          slot: GoogleAdsSlotFactory.lb1("newsletter/sign-up"),
          adsClassName:
            "min-h-[90px] flex flex-col justify-center items-center",
        }}
      />

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial("newsletter/sign-up")}
        {...{ isPrestitialEnabled, topOverlayImpressions, topOverlayValidity }}
      />

      <Header />

      <Container>
        <h1
          className={cx(
            "border-b border-gray-850 pb-3 pt-6 font-playfair text-8xl uppercase"
          )}
        >
          Newsletters
        </h1>
      </Container>

      <div
        className="newsletter-signup-page container !mt-6 px-3"
        data-testid="newsletter-signup-page"
      >
        <div className="grid grid-cols-12 gap-3 md:gap-0">
          <div className="col-span-12 xl:col-span-3">
            <p className="font-public-sans text-lg font-normal text-gray-850">
              Select the newsletters that suit your needs and get them sent
              straight to your inbox.
            </p>

            <NewsletterAnchorLinks
              scrollToNewsletterSection={(newsletter) => {
                scrollToNewsletterSection(newsletter, weeklyNewsletterRef);
              }}
            />
          </div>

          <div className="col-span-12 xl:col-span-9">
            <NewsletterSections
              title="Premium NEWSLETTERS"
              newsletters={PREMIUM_NEWSLETTERS}
              selectedNewsletters={selectedNewsletters}
              handleSelectedNewsletter={handleSelectedNewsletter}
              sectionRef={premiumNewsletterRef}
            />

            <NewsletterSections
              title="Daily NEWSLETTERS"
              newsletters={DAILY_NEWSLETTERS}
              selectedNewsletters={selectedNewsletters}
              handleSelectedNewsletter={handleSelectedNewsletter}
              sectionRef={dailyNewsletterRef}
              rootClassname={"xl:pt-0 xl:border-none pt-3 border-t"}
            />

            <NewsletterSections
              title="Weekly NEWSLETTERS"
              newsletters={WEEKLY_NEWSLETTERS}
              selectedNewsletters={selectedNewsletters}
              handleSelectedNewsletter={handleSelectedNewsletter}
              sectionRef={weeklyNewsletterRef}
            />
          </div>
        </div>
      </div>

      {selectedNewsletters.length > 0 ? (
        <NewsletterSubscriptionPanel
          selectedNewsletters={selectedNewsletters}
          handleSelectedNewsletter={handleSelectedNewsletter}
          email={email}
          setEmail={setEmail}
        />
      ) : null}

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.CATFISH,
          slot: GoogleAdsSlotFactory.catfish("newsletter/sign-up"),
        }}
      />

      <Advertisement
        adUnitProps={{
          type: AdvertisementTypeEnum.ABM,
          slot: GoogleAdsSlotFactory.abm(),
        }}
      />
    </div>
  );
}
